// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import EnhancedTableHead from "../ui/EnhancedTableHead";
import "../../css/Reports.scss";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import wso2Theme from "../../theme";
import useHttp from "../../utils/http";
import dayjs from "dayjs";
import { postSearchPerdiemPayments } from "../../utils/end-points";
import BackdropProgress from "../../BackdropProgress";
import CustomSnackbar from "../ui/Snackbars";
import TablePagination from "@mui/material/TablePagination";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import LinearProgress from "@mui/material/LinearProgress";
import { FILTER_DATE_FORMAT } from "../../Config";
import { escapeCsv } from "../../utils/formatting";

const defaultColumns = [
  { key: "perDiemNumber", label: "Per Diem Number", checked: true },
  { key: "travellerEmail", label: "Traveller WSO2 Email", checked: true },
  { key: "jobNumber", label: "Job Number", checked: true },
  { key: "netsuiteStatus", label: "Netsuite Status", checked: true },
  { key: "paymentCurrency", label: "Payment Currency", checked: true },
  { key: "paymentAmount", label: "Payment Amount", checked: true },
  { key: "convertedCurrency", label: "Converted Currency", checked: true },
  { key: "convertedAmount", label: "Converted Amount", checked: true },
];

const optionalColumns = [
  { key: "addedDate", label: "Added Date", checked: false },
];

function PerDiemPaymentsReport() {
  const [startDate, setStartDate] = useState(
    dayjs().startOf("year").format(FILTER_DATE_FORMAT)
  );
  const [endDate, setEndDate] = useState(dayjs().format(FILTER_DATE_FORMAT));
  const [isPageLoading, setPageLoading] = useState(true);
  const { handleRequest } = useHttp();
  const [payments, setPayments] = useState([]);
  const [snackbarData, setSnackbarData] = useState({
    open: false,
    message: "",
    severity: "",
    onClose: null,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [showFilters, setShowFilters] = useState(false);
  const [isTableLoading, setTableLoading] = useState(false);

  const [columns, setColumns] = useState([
    ...defaultColumns,
    ...optionalColumns,
  ]);

  useEffect(() => {
    fetchPayments();
  }, [startDate, endDate]);

  const fetchPayments = () => {
    handleRequest(
      postSearchPerdiemPayments,
      "POST",
      { startDate, endDate },
      (response) => {
        setPayments(response);
        setPageLoading(false);
        setTableLoading(false);
      },
      (error) => {
        setSnackbarData({
          message: error,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
        setPageLoading(false);
      },
      setTableLoading
    );
  };

  const onCloseSnackbar = () => {
    setSnackbarData({ ...snackbarData, open: false });
  };

  const paginatedPayments = payments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleColumn = (key) => {
    setColumns(
      columns.map((col) =>
        col.key === key ? { ...col, checked: !col.checked } : col
      )
    );
  };

  const downloadCsv = () => {
    const selectedColumns = columns.filter((col) => col.checked);
    if (!selectedColumns.length || !payments.length) return;
    const headers = selectedColumns.map((col) => escapeCsv(col.label));
    const rows = payments.map((payment) =>
      selectedColumns.map((col) => escapeCsv(payment[col.key]))
    );

    const csvContent = [headers, ...rows]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `${startDate}_to_${endDate}_perdiem.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="Reports-table">
      <BackdropProgress open={isPageLoading} />
      <CustomSnackbar snackbar={snackbarData} />
      {!isPageLoading && (
        <Grid container>
          <Grid item lg={12} className="Table-grid">
            <Paper className="Border-elevation">
              <div
                className="Search-and-filters"
                style={{ borderColor: wso2Theme.palette.primary.light }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    padding: 2,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Start Date"
                        value={dayjs(startDate)}
                        format="YYYY/MM/DD"
                        onChange={(newValue) =>
                          setStartDate(
                            dayjs(newValue).format(FILTER_DATE_FORMAT)
                          )
                        }
                        renderInput={(params) => (
                          <TextField {...params} size="small" sx={{ my: 0 }} />
                        )}
                      />
                      <DatePicker
                        label="End Date"
                        value={dayjs(endDate)}
                        format="YYYY/MM/DD"
                        onChange={(newValue) =>
                          setEndDate(dayjs(newValue).format(FILTER_DATE_FORMAT))
                        }
                        renderInput={(params) => (
                          <TextField {...params} size="small" sx={{ my: 0 }} />
                        )}
                      />
                    </LocalizationProvider>

                    <Button
                      variant={showFilters ? "outlined" : "contained"}
                      startIcon={<FilterListIcon />}
                      color="primary"
                      className="Filter-button"
                      onClick={() => setShowFilters(!showFilters)}
                      sx={{ textTransform: "none" }}
                    >
                      {showFilters ? "Hide Fields" : "Show Fields"}
                    </Button>
                  </Box>

                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={downloadCsv}
                    startIcon={<DownloadIcon />}
                    sx={{ textTransform: "none" }}
                  >
                    Download CSV
                  </Button>
                </Box>

                {showFilters && (
                  <Box sx={{ paddingX: 2, marginBottom: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            pb: 1,
                            pt: 2,
                            borderBottom: "1px solid #ddd",
                            boxShadow: "inset 0 -2px 3px -2px rgba(0,0,0,0.2)",
                          }}
                        >
                          Select columns to display in the table and include in
                          CSV download
                        </Box>
                        <Grid container spacing={1}>
                          {defaultColumns.map((col) => (
                            <Grid item xs={6} sm={3} md={2} key={col.key}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    color="secondary"
                                    checked={
                                      columns.find((c) => c.key === col.key)
                                        .checked
                                    }
                                    onChange={() => toggleColumn(col.key)}
                                    size="small"
                                  />
                                }
                                label={col.label}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider />
                        <Grid container spacing={1}>
                          {optionalColumns.map((col) => (
                            <Grid item xs={6} sm={3} md={2} key={col.key}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    color="secondary"
                                    checked={
                                      columns.find((c) => c.key === col.key)
                                        .checked
                                    }
                                    onChange={() => toggleColumn(col.key)}
                                    size="small"
                                  />
                                }
                                label={col.label}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Grid container>
                  <Grid item lg={12} className="Table-grid">
                    {isTableLoading && <LinearProgress color="secondary" />}
                    <TableContainer sx={{ overflowX: "auto" }}>
                      <Table size="small" sx={{ minWidth: 1200 }}>
                        <EnhancedTableHead
                          headCells={columns
                            .filter((col) => col.checked)
                            .map((col) => ({
                              id: col.key,
                              label: col.label,
                              align: "center",
                              disablePadding: false,
                            }))}
                          sx={{ "& th": { paddingX: 3, paddingY: 1.5 } }}
                        />
                        <TableBody>
                          {paginatedPayments.length > 0 ? (
                            paginatedPayments.map((payment, index) => (
                              <TableRow
                                hover
                                tabIndex={-1}
                                key={payment.id || index}
                                className="Table-row"
                              >
                                {columns
                                  .filter((col) => col.checked)
                                  .map((col) => (
                                    <TableCell
                                      key={col.key}
                                      className="Table-cell"
                                      sx={{ whiteSpace: "nowrap" }}
                                    >
                                      {payment[col.key]}
                                    </TableCell>
                                  ))}
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell
                                colSpan={
                                  columns.filter((col) => col.checked).length
                                }
                                align="center"
                                className="No-content"
                              >
                                No per diem payments found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <div
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <TablePagination
                        component="div"
                        count={payments.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 12, 25, 50]}
                      />
                    </div>
                  </Grid>
                </Grid>
              </div>
            </Paper>
          </Grid>
        </Grid>
      )}
    </div>
  );
}

export default PerDiemPaymentsReport;
